import { formatDistanceToNowStrict } from "date-fns";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { text } from "@/lib/format";

const APP_META = {
  web: { label: "Brugo Web", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  home: { label: "Customer", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pro: { label: "Professional", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  ops: { label: "Command Center", cls: "bg-primary/10 text-primary border-primary/20" },
  functions: { label: "Cloud Functions", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  rules: { label: "Firestore Rules", cls: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  system: { label: "System", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STATUS_META = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
  deployed: { label: "Deployed", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
};

const ts = (it) => it.server_ts || it.created_at;
const appKey = (it) => it.app || "system";
const change = (it) => it.change_type || it.action || "activity";
const desc = (it) => it.description || (it.metadata && typeof it.metadata === "object" ? Object.entries(it.metadata).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(", ") : (it.entity_type ? `${it.entity_type} ${it.entity_id || ""}` : ""));
const who = (it) => text(it.changed_by || it.performed_by || "system");

function ago(t) {
  if (!t) return "—";
  try {
    const d = t?.toDate ? t.toDate() : (t?.seconds != null ? new Date(t.seconds * 1000) : new Date(t));
    return isNaN(d) ? "—" : formatDistanceToNowStrict(d, { addSuffix: true });
  } catch { return "—"; }
}

export function ActivityList({ items, canApprove, onApprove, onReject, compact }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="activity-empty">No activity yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="activity-list">
      {items.map((it) => {
        const am = APP_META[appKey(it)] || APP_META.system;
        const sm = it.status ? STATUS_META[it.status] : null;
        return (
          <div key={it.id} className="rounded-lg border border-border p-3 hover:bg-accent/40 transition-colors" data-testid={`activity-row-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${am.cls}`}>{am.label}</span>
                  <span className="text-sm font-medium text-foreground truncate">{change(it)}</span>
                  {it.is_demo && <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1">demo</span>}
                  {sm && <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${sm.cls}`} data-testid={`activity-status-${it.id}`}>{sm.label}</span>}
                </div>
                {desc(it) && <p className="mt-1 text-xs text-muted-foreground break-words">{desc(it)}</p>}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-muted-foreground">
                  <span className="flex items-center gap-1"><Icons.User className="h-3 w-3" />{who(it)}{it.performed_by_role ? ` · ${it.performed_by_role}` : ""}</span>
                  <span className="flex items-center gap-1"><Icons.Clock className="h-3 w-3" />{ago(ts(it))}</span>
                </div>
              </div>
              {!compact && canApprove && it.status === "pending" && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button size="sm" className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-500" onClick={() => onApprove(it)} data-testid={`approve-${it.id}`}><Icons.Check className="h-3.5 w-3.5" /> Approve</Button>
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-red-600 border-red-200" onClick={() => onReject(it)} data-testid={`reject-${it.id}`}><Icons.X className="h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const ACTIVITY_APPS = APP_META;
